import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { createSlice } from '@reduxjs/toolkit'
import { getAdjustedIndexAndTimestamp } from '../components/Utilities';

dayjs.extend(customParseFormat);

const dataSlice = createSlice({
    name: 'data',
    initialState: {
        source: null,
        index: null,
        overViewCameraId: null,
        initialFooterHeight: 48,
        timestamp: dayjs(),
        timestampIsDefault: true, // true until cleared by setTimestamp or by the first setSource — flips the first setSource into "land on latest image" mode
        download: { loading: false, progress: 0 },
        generation: { loading: false, progress: 0 },
        checkedArchivePictures: [],
        token: null,
        notification: {},
        isWeatherAlarmViaManagerMessage: false,
        subscriptionId: null,
        accessMonitoringCameraId: null,
        accessMonitoringVehicleFilter: null, // null = show all, [] = show nothing, [...keys] = show only these
        siderWidth: undefined, // width of the MySite/Archive sider content area (set by ArchiveControls' ResizeObserver, read by sider children)
        featureFlags: {
            hasTimelapse: false,
            hasDashboard: false,
            hasAccessMonitoring: false,
            accessReady: false, // async gate (has data) -> corresponding readiness flag needed
            hasReport: false,
            reportReady: false, // async gate (file check) -> corresponding readiness flag needed
            hasDiary: false,
            hasDownload: false
        }
    },
    reducers: {
        setSource: (state, { payload }) => {
            state.source = payload;
            if (payload) {
                const list = payload[0]
                if (!list?.length) return
                const lastIdx = list.length - 1
                const lastTimestamp = list[lastIdx]?.[0]
                if (state.timestampIsDefault) {
                    state.index = lastIdx
                    state.timestamp = dayjs(lastTimestamp)
                } else {
                    const hasValidTimestamp = state.timestamp && dayjs.isDayjs(state.timestamp) && state.timestamp.isValid()
                    const lookupTimestamp = hasValidTimestamp ? state.timestamp : lastTimestamp
                    const indexAndTimestamp = getAdjustedIndexAndTimestamp(payload, lookupTimestamp)
                    state.index = indexAndTimestamp.index
                    state.timestamp = indexAndTimestamp.timestamp
                }
                state.timestampIsDefault = false
            }
        },
        setIndex: (state, { payload }) => {
            state.index = payload;
        },
        setTimestamp: (state, { payload }) => {

            let dayJsFromDate;

            if (/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/.test(payload)) { // check if payload is in "DD.MM.YYYY HH:mm" format
                dayJsFromDate = dayjs(payload, "DD.MM.YYYY HH:mm", true);
            } else { // payload is e.g. "Mon Jun 30 2025 13:45:00 GMT+0200 (Mitteleuropäische Sommerzeit)
                dayJsFromDate = dayjs(payload);
            }

            if (dayJsFromDate.isValid() && !state.timestamp.isSame(dayJsFromDate)) {
                state.timestamp = dayJsFromDate
                state.timestampIsDefault = false // explicit set — survives subsequent setSource calls
                if (state.source && payload) {
                    let indexAndTimestamp = getAdjustedIndexAndTimestamp(state.source, dayJsFromDate)
                    state.index = indexAndTimestamp.index;
                    state.timestamp = indexAndTimestamp.timestamp;
                }
            }
        },
        setOverviewCameraId: (state, { payload }) => {
            state.overViewCameraId = payload;
        },
        setInitialFooterHeight: (state, { payload }) => {
            state.initialFooterHeight = payload
        },
        loadPrevImage: (state) => {
            const decrementedIndex = state.index - 1;
            if (state.source[0]?.[decrementedIndex]) {
                state.index = decrementedIndex;
                state.timestamp = dayjs(state.source[0][decrementedIndex][0]);
            }
        },
        loadNextImage: (state) => {
            const incrementedIndex = state.index + 1;
            if (state.source[0]?.[incrementedIndex]) {
                state.index = incrementedIndex;
                state.timestamp = dayjs(state.source[0][incrementedIndex][0]);
            }
        },
        setDownload: (state, { payload }) => {
            state.download = { ...state.download, ...payload }
        },
        setGeneration: (state, { payload }) => {
            state.generation = { ...state.generation, ...payload }
        },
        setCheckedArchivePictures: (state, { payload }) => {
            const type = payload.type
            const action = payload.action
            if (type === 'clean') {
                state.checkedArchivePictures = []
            } else if (type === 'add') {
                state.checkedArchivePictures.push(action.value)
            } else if (type === 'remove') {
                state.checkedArchivePictures.splice(action.index, 1)
            }
        },
        setToken: (state, { payload }) => {
            window.history.replaceState({}, "", window.location.pathname + window.location.search) // remove hash from URL
            state.token = payload
        },
        setNotification: (state, { payload }) => {
            state.notification = payload
        },
        resetNotification: (state) => {
            state.notification = {}
        },
        setIsWeatherAlarmViaManagerMessage: (state, { payload }) => {
            state.isWeatherAlarmViaManagerMessage = payload
        },
        setSubscriptionId: (state, { payload }) => {
            state.subscriptionId = payload
        },
        setAccessMonitoringCameraId: (state, { payload }) => {
            state.accessMonitoringCameraId = payload
        },
        setAccessMonitoringVehicleFilter: (state, { payload }) => {
            state.accessMonitoringVehicleFilter = payload
        },
        setSiderWidth: (state, { payload }) => {
            if (state.siderWidth !== payload) state.siderWidth = payload
        },
        setFeatureFlag: (state, { payload: { name, value } }) => {
            if (state.featureFlags[name] !== value) state.featureFlags[name] = value
        }
    }
})

export const {
    setSource,
    setIndex,
    setTimestamp,
    setOverviewCameraId,
    setInitialFooterHeight,
    loadPrevImage,
    loadNextImage,
    setDownload,
    setGeneration,
    setCheckedArchivePictures,
    setToken,
    setNotification,
    resetNotification,
    setIsWeatherAlarmViaManagerMessage,
    setSubscriptionId,
    setAccessMonitoringCameraId,
    setAccessMonitoringVehicleFilter,
    setSiderWidth,
    setFeatureFlag
} = dataSlice.actions

export const getData = state => state.data

export default dataSlice.reducer